import type { LeadIntakeMeta } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { formatNumericDateTime } from "../../i18n/format";
import type { Translate } from "../../i18n/locale";
import { preloadFacebookSdk, readyFacebookSdk, type FacebookSdk } from "../../whatsapp/meta-signup";

// «Collega la pagina Facebook» dei moduli di Meta (piano del 12 ottobre 2026, I3, D1; INFRA-9): l'accesso di Facebook con lo stesso
// SDK del WhatsApp ufficiale (`whatsapp/meta-signup.ts`: uno `<script>` per pagina, nei test finto), caricato soltanto nel riquadro
// dei moduli di Meta di Impostazioni → «Ingressi dei Lead», appena si vede il pulsante: così `FB.login` parte subito dentro il clic e
// il browser non blocca la finestra. `FB.login` chiede i permessi per leggere le pagine, i loro moduli e i loro Lead
// (`META_PAGES_SCOPES`), con `return_scopes` per sapere quali sono stati dati e `auth_type: "rerequest"` per richiedere quelli negati
// la volta prima. **Mai `pages_manage_metadata`**: Meta lo rifiuta senza l'esame dell'app e blocca tutto l'accesso. Il gettone utente di
// breve durata va subito al server (che lo scambia e non lo restituisce) e non si salva nel browser.

/** I permessi chiesti a Facebook: le pagine, i loro moduli e i loro Lead. */
export const META_PAGES_SCOPES = ["pages_show_list", "pages_read_engagement", "pages_manage_ads", "leads_retrieval", "business_management"] as const;
/** I permessi senza i quali i moduli non si leggono: senza, l'accesso non vale. */
export const META_REQUIRED_SCOPES = ["leads_retrieval", "pages_manage_ads"] as const;

export type FacebookPagesLoginOptions = { scope: string; return_scopes: true; auth_type: "rerequest" };
export type FacebookPagesLoginResponse = { authResponse?: { accessToken?: string | null; grantedScopes?: string | null } | null; status?: string };
type FacebookPagesLogin = { login(callback: (response: FacebookPagesLoginResponse) => void, options: FacebookPagesLoginOptions): void };

/** Le opzioni di `FB.login` per le pagine. */
export function metaPagesLoginOptions(): FacebookPagesLoginOptions {
  return { scope: META_PAGES_SCOPES.join(","), return_scopes: true, auth_type: "rerequest" };
}

/** Com'è finito l'accesso: con il gettone, chiuso prima della fine, senza SDK o senza i permessi dei moduli. */
export type MetaPagesLoginOutcome = { kind: "granted"; accessToken: string } | { kind: "cancelled" } | { kind: "failed"; reason: "sdk_unavailable" | "permissions" };

export type MetaSdkConfig = { appId: string; graphVersion: string; locale: Locale };

/** Chiede l'SDK prima che serva, quando il riquadro mostra «Collega la pagina Facebook»; uno che non arriva si richiede al clic. */
export function preloadMetaPagesSdk(config: MetaSdkConfig): void {
  preloadFacebookSdk(config).catch(() => undefined);
}

/** L'esito di una risposta di `FB.login` (esportato per i test). */
export function pagesLoginOutcomeOf(response: FacebookPagesLoginResponse): MetaPagesLoginOutcome {
  const token = response.authResponse?.accessToken;
  if (typeof token !== "string" || token === "") return { kind: "cancelled" };
  const granted = new Set((response.authResponse?.grantedScopes ?? "").split(",").map((scope) => scope.trim()));
  // Senza `grantedScopes` Facebook non dice quali permessi ha dato: decide il server, che prova a leggere i moduli.
  if (response.authResponse?.grantedScopes && !META_REQUIRED_SCOPES.every((scope) => granted.has(scope))) return { kind: "failed", reason: "permissions" };
  return { kind: "granted", accessToken: token };
}

function loginWith(sdk: FacebookSdk): Promise<MetaPagesLoginOutcome> {
  return new Promise((resolve) => {
    try {
      // Lo stesso SDK, con le opzioni dell'accesso alle pagine invece di quelle dell'Embedded Signup.
      (sdk as unknown as FacebookPagesLogin).login((response) => resolve(pagesLoginOutcomeOf(response)), metaPagesLoginOptions());
    } catch {
      resolve({ kind: "failed", reason: "sdk_unavailable" });
    }
  });
}

/** Apre l'accesso di Facebook per le pagine: con l'SDK pronto `FB.login` parte subito, altrimenti appena l'SDK arriva. */
export function openMetaPagesLogin(config: MetaSdkConfig): Promise<MetaPagesLoginOutcome> {
  const ready = readyFacebookSdk();
  if (ready) return loginWith(ready);
  return preloadFacebookSdk(config).then(loginWith, (): MetaPagesLoginOutcome => ({ kind: "failed", reason: "sdk_unavailable" }));
}

/** Se l'ultimo giro dice che Meta non accetta più il collegamento: il gettone (190) o la cifratura cambiata. */
export function metaNeedsReconnect(meta: Pick<LeadIntakeMeta, "lastRound">): boolean {
  const error = meta.lastRound?.error ?? null;
  return error !== null && (error === "190" || error.startsWith("190:") || error === "token_unreadable");
}

/** Che cosa dice l'ultimo giro di lettura dei moduli, in parole. */
export function metaRoundText(t: Translate, timeZone: string, meta: Pick<LeadIntakeMeta, "lastRound">): { tone: "info" | "warning"; text: string } {
  const round = meta.lastRound;
  if (round === null) return { tone: "info", text: t("leadIntakeSettings.metaRoundNone") };
  if (metaNeedsReconnect(meta)) return { tone: "warning", text: t("leadIntakeSettings.metaRoundTokenRejected") };
  const when = formatNumericDateTime(timeZone, round.at);
  return round.error === null ? { tone: "info", text: t("leadIntakeSettings.metaRoundOk", { when }) } : { tone: "warning", text: t("leadIntakeSettings.metaRoundFailed", { when }) };
}
